/**
 * Redirect Manager
 * Table of 301/302 redirects with create/edit/delete
 */

import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  Edit3,
  Check,
  X,
  ArrowRight,
  ExternalLink,
  Search,
} from 'lucide-react';
import type { Redirect } from '../../types';

// ============================================================================
// TYPES
// ============================================================================

interface RedirectManagerProps {
  redirects: Redirect[];
  onSave: (redirect: Partial<Redirect>) => Promise<void>;
  onDelete: (redirectId: string) => Promise<void>;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function RedirectManager({ redirects, onSave, onDelete }: RedirectManagerProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newRedirect, setNewRedirect] = useState<Partial<Redirect> | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = redirects.filter(r =>
    !searchQuery ||
    r.sourcePath.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.targetUrl.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const startNew = () => {
    setNewRedirect({ sourcePath: '', targetUrl: '', redirectType: 301, isActive: true });
    setEditingId(null);
  };

  const saveNew = async () => {
    if (!newRedirect?.sourcePath || !newRedirect?.targetUrl) return;
    await onSave(newRedirect);
    setNewRedirect(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-gray-900">URL Redirects</h3>
          <span className="text-sm text-gray-500">{redirects.length} rules</span>
        </div>
        <button
          onClick={startNew}
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" /> Add Redirect
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search redirects..."
          className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
        />
      </div>

      {/* New Redirect Form */}
      {newRedirect && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <input
            type="text"
            value={newRedirect.sourcePath || ''}
            onChange={(e) => setNewRedirect({ ...newRedirect, sourcePath: e.target.value })}
            placeholder="/old-path"
            className="flex-1 border rounded px-2 py-1.5 text-sm font-mono"
            autoFocus
          />
          <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={newRedirect.targetUrl || ''}
            onChange={(e) => setNewRedirect({ ...newRedirect, targetUrl: e.target.value })}
            placeholder="https://example.com/new-path"
            className="flex-1 border rounded px-2 py-1.5 text-sm font-mono"
          />
          <select
            value={newRedirect.redirectType || 301}
            onChange={(e) => setNewRedirect({ ...newRedirect, redirectType: Number(e.target.value) })}
            className="border rounded px-2 py-1.5 text-sm w-20"
          >
            <option value={301}>301</option>
            <option value={302}>302</option>
          </select>
          <button onClick={saveNew} className="p-1.5 hover:bg-green-100 rounded text-green-600">
            <Check className="h-4 w-4" />
          </button>
          <button onClick={() => setNewRedirect(null)} className="p-1.5 hover:bg-gray-200 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Redirect Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Source</th>
              <th className="w-8"></th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Target</th>
              <th className="text-center text-xs font-medium text-gray-500 uppercase px-4 py-3 w-16">Type</th>
              <th className="text-center text-xs font-medium text-gray-500 uppercase px-4 py-3 w-16">Hits</th>
              <th className="text-center text-xs font-medium text-gray-500 uppercase px-4 py-3 w-16">Status</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map((redirect) => (
              <tr key={redirect.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-mono text-gray-700">{redirect.sourcePath}</td>
                <td><ArrowRight className="h-3 w-3 text-gray-400" /></td>
                <td className="px-4 py-3 text-sm font-mono text-blue-600">{redirect.targetUrl}</td>
                <td className="text-center px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    redirect.redirectType === 301 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {redirect.redirectType}
                  </span>
                </td>
                <td className="text-center px-4 py-3 text-sm text-gray-500">
                  {redirect.hitCount || 0}
                </td>
                <td className="text-center px-4 py-3">
                  <span className={`inline-block h-2 w-2 rounded-full ${
                    redirect.isActive ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onDelete(redirect.id)}
                    className="p-1 hover:bg-red-100 rounded text-red-500 opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-sm">
            No redirects configured
          </div>
        )}
      </div>
    </div>
  );
}

export default RedirectManager;
